import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { firestoreService } from "@/services/firestoreService";
import { Timestamp } from "firebase/firestore";
import { Pagination } from "@/components/ui/Pagination";
import { DateRangeFilter } from "@/components/ui/DateRangeFilter";
import { Input } from "@/components/ui/Input";

interface NewsArticle {
  id: string;
  title: string;
  summary?: string;
  source: string;
  url?: string;
  publishedAt: any;
  companyId?: string;
  companyName?: string;
  ticker?: string;
  tags?: string[];
}

function isCompanyScopedArticle(article: NewsArticle): boolean {
  const id = article.companyId?.trim();
  const name = article.companyName?.trim();
  return Boolean(id || name);
}

/** Only show a summary row when there is real content (not empty / placeholder). */
function usableSummary(summary?: string): string | undefined {
  if (summary == null) return undefined;
  const t = summary.trim();
  if (!t) return undefined;
  if (t.toLowerCase() === "summary unavailable") return undefined;
  return t;
}

export const News: React.FC = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<
    "all" | "blazerex" | "market" | "company"
  >("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    loadNews();
  }, [filter, currentPage, itemsPerPage, startDate, endDate, debouncedSearch]);

  const loadNews = async () => {
    try {
      setLoading(true);
      
      // Handle Blazerex Articles separately
      if (filter === "blazerex") {
        const articles = await firestoreService.getDocuments<any>(
          "blazerexArticles",
          [
            firestoreService.orderBy("publishedAt", "desc"),
            firestoreService.limit(itemsPerPage * currentPage),
          ]
        );
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedArticles = articles.slice(startIndex, endIndex);
        setNews(paginatedArticles.map(article => ({
          id: article.id,
          title: article.title,
          summary: usableSummary(article.summary || article.excerpt),
          source: "Blazerex",
          url: article.url,
          publishedAt: article.publishedAt,
          tags: article.tags || [],
        })));
        setTotalPages(Math.ceil(articles.length / itemsPerPage));
        setLoading(false);
        return;
      }
      
      const constraints: any[] = [];

      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        constraints.push(
          firestoreService.where("publishedAt", ">=", Timestamp.fromDate(start))
        );
      }

      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        constraints.push(
          firestoreService.where("publishedAt", "<=", Timestamp.fromDate(end))
        );
      }

      constraints.push(firestoreService.orderBy("publishedAt", "desc"));

      const pageNeed = itemsPerPage * currentPage;
      const categoryFilteredFetch =
        filter === "market" || filter === "company";
      const fetchLimit = categoryFilteredFetch
        ? Math.min(800, pageNeed * 20)
        : pageNeed;
      constraints.push(firestoreService.limit(fetchLimit));

      const allArticles = await firestoreService.getDocuments<NewsArticle>(
        "news",
        constraints
      );

      let scopedArticles = allArticles;
      if (filter === "market") {
        scopedArticles = allArticles.filter((a) => !isCompanyScopedArticle(a));
      } else if (filter === "company") {
        scopedArticles = allArticles.filter((a) => isCompanyScopedArticle(a));
      }

      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedArticles = scopedArticles.slice(startIndex, endIndex);

      const q = debouncedSearch.trim().toLowerCase();
      const filtered = q
        ? paginatedArticles.filter(
            (article) =>
              article.title?.toLowerCase().includes(q) ||
              article.companyName?.toLowerCase().includes(q) ||
              article.ticker?.toLowerCase().includes(q)
          )
        : paginatedArticles;

      setNews(filtered);
      setTotalPages(Math.ceil(scopedArticles.length / itemsPerPage));
    } catch (error) {
      console.error("Failed to load news:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="h1 text-4xl mb-2">Financial News</h1>
          <p className="body-text text-gray-600">
            Latest Financial News
          </p>
        </div>

        <Card className="mb-6 p-6">
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="body-text text-sm text-gray-600 mb-1 block">
                  Search News
                </label>
                <Input
                  type="text"
                  placeholder="Search by title, company, or ticker"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
              <DateRangeFilter
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={(date) => {
                  setStartDate(date);
                  setCurrentPage(1);
                }}
                onEndDateChange={(date) => {
                  setEndDate(date);
                  setCurrentPage(1);
                }}
                onClear={() => {
                  setStartDate("");
                  setEndDate("");
                  setCurrentPage(1);
                }}
              />
              <Button variant="outline" onClick={handleClearFilters}>
                Clear All
              </Button>
            </div>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => {
                  setFilter("all");
                  setCurrentPage(1);
                }}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  filter === "all"
                    ? "bg-blazerex-green text-blazerex-black font-header-bold"
                    : "bg-white border border-gray-300 text-gray-700 hover:border-blazerex-green"
                }`}
              >
                All News
              </button>
              <button
                onClick={() => {
                  setFilter("blazerex");
                  setCurrentPage(1);
                }}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  filter === "blazerex"
                    ? "bg-blazerex-green text-blazerex-black font-header-bold"
                    : "bg-white border border-gray-300 text-gray-700 hover:border-blazerex-green"
                }`}
              >
                Blazerex Articles
              </button>
              <button
                onClick={() => {
                  setFilter("market");
                  setCurrentPage(1);
                }}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  filter === "market"
                    ? "bg-blazerex-green text-blazerex-black font-header-bold"
                    : "bg-white border border-gray-300 text-gray-700 hover:border-blazerex-green"
                }`}
              >
                Market News
              </button>
              <button
                onClick={() => {
                  setFilter("company");
                  setCurrentPage(1);
                }}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  filter === "company"
                    ? "bg-blazerex-green text-blazerex-black font-header-bold"
                    : "bg-white border border-gray-300 text-gray-700 hover:border-blazerex-green"
                }`}
              >
                Company News
              </button>
            </div>
          </div>
        </Card>

        {news.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <p className="body-text text-gray-600">
                No news available yet. News articles are updated regularly.
              </p>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {news.map((article) => {
              const summaryText = usableSummary(article.summary);
              return (
              <Card key={article.id} hover>
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    {article.companyName && (
                      <div className="flex items-center gap-2 mb-2">
                        <span className="body-text text-sm font-mono text-blazerex-green">
                          {article.ticker}
                        </span>
                        <span className="body-text text-sm text-gray-500">
                          {article.companyName}
                        </span>
                      </div>
                    )}
                    <h3 className="sub-header text-xl mb-2">{article.title}</h3>
                    {summaryText ? (
                      <p className="body-text text-gray-600 mb-3">
                        {summaryText}
                      </p>
                    ) : null}
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{article.source}</span>
                      {article.publishedAt && (
                        <span>
                          {new Date(
                            article.publishedAt.toDate?.() ||
                              article.publishedAt
                          ).toLocaleDateString()}
                        </span>
                      )}
                      {article.tags && article.tags.length > 0 && (
                        <div className="flex gap-2">
                          {article.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-1 bg-gray-100 rounded text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {filter === "blazerex" ? (
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/blazerex-article/${article.id}`)}
                    >
                      Read Article →
                    </Button>
                  ) : article.url ? (
                    <a
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blazerex-green hover:underline"
                    >
                      Read More →
                    </a>
                  ) : null}
                </div>
              </Card>
            );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(items) => {
                setItemsPerPage(items);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
